const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const toInt = (value, fallback = 0) => {
  const num = parseInt(value, 10);
  return Number.isFinite(num) ? num : fallback;
};

const normalizeText = (value) => {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

const normalizeOptionalFormatName = (value) => {
  const text = String(value ?? '').trim();
  const normalized = normalizeText(text);
  if (!normalized || ['0', 'none', 'null', 'undefined'].includes(normalized)) return null;
  return text;
};

const getProductId = (row) => {
  const pid =
    row?.product_id ??
    row?.productId ??
    row?.id_product ??
    row?.idProduct ??
    row?.product?.id ??
    row?.product?.product_id ??
    row?.product?.id_product ??
    row?.product;
  const parsed = toInt(pid, 0);
  return parsed > 0 ? parsed : null;
};

const getSupplierId = (row) => {
  const supplierId =
    row?.supplier_id ??
    row?.supplierId ??
    row?.provider_id ??
    row?.providerId ??
    row?.supplier?.id ??
    row?.provider?.id ??
    row?.product?.supplier_id ??
    row?.product?.supplierId ??
    row?.product?.supplier?.id ??
    null;
  const parsed = toInt(supplierId, 0);
  return parsed > 0 ? parsed : null;
};

const getDirectFormatId = (row) => {
  const formatId = row?.order_format_missing === true
    ? row?.order_detail_format_id
    : (
      row?.order_detail_format_id ??
      row?.prformat_id ??
      row?.prformatId ??
      row?.format_id ??
      row?.formatId ??
      row?.presentation_id ??
      row?.presentationId ??
      row?.formato_id ??
      row?.formatoId ??
      row?.unit_id ??
      row?.product_format_id ??
      row?.prformat?.id ??
      row?.format?.id ??
      row?.presentation?.id ??
      row?.formato?.id ??
      row?.unit?.id ??
      row?.product_format?.id ??
      null
    );
  const parsedId = toInt(formatId, 0);
  return parsedId > 0 ? parsedId : null;
};

const getInferredFormatId = (row) => {
  const formatId =
    row?.inferred_format_id ??
    row?.inferredFormatId ??
    row?.product?.prformat_id ??
    row?.product?.format_id ??
    row?.product?.prformat?.id ??
    row?.product?.format?.id ??
    null;
  const parsedId = toInt(formatId, 0);
  return parsedId > 0 ? parsedId : null;
};

const getFormatId = (row) => getDirectFormatId(row) ?? getInferredFormatId(row);

const getDirectRawFormatName = (row) => {
  const raw = row?.order_format_missing === true
    ? row?.order_detail_format_name
    : (
      row?.order_detail_format_name ??
      row?.prformat?.name ??
      row?.format?.name ??
      row?.presentation?.name ??
      row?.formato?.name ??
      row?.unit?.name ??
      row?.product_format?.name ??
      row?.prformat_name ??
      row?.format_name ??
      row?.presentation_name ??
      row?.formato_name ??
      row?.unit_name ??
      row?.formato ??
      row?.prformat ??
      row?.format ??
      row?.presentation ??
      row?.unit ??
      row?.product_format ??
      ''
    );

  if (raw && typeof raw === 'object') {
    return normalizeOptionalFormatName(raw?.name ?? raw?.label ?? raw?.value);
  }
  return normalizeOptionalFormatName(raw);
};

const getInferredRawFormatName = (row) => {
  const raw =
    row?.inferred_format_name ??
    row?.inferredFormatName ??
    row?.product?.prformat?.name ??
    row?.product?.format?.name ??
    row?.product?.prformat_name ??
    row?.product?.format_name ??
    row?.product?.prformat ??
    row?.product?.format ??
    '';

  if (raw && typeof raw === 'object') {
    return normalizeOptionalFormatName(raw?.name ?? raw?.label ?? raw?.value);
  }
  return normalizeOptionalFormatName(raw);
};

const getRawFormatName = (row) => getDirectRawFormatName(row) ?? getInferredRawFormatName(row);

const getFormatName = (row) => {
  const normalized = normalizeText(getRawFormatName(row));
  return normalized || null;
};

const isOrderFormatMissing = (row) => row?.order_format_missing === true;

export const normalizeInvoiceLineFormat = (row) => {
  const source = row || {};
  const formatId = getFormatId(source);
  const formatName = getRawFormatName(source);
  return {
    ...source,
    prformat_id: formatId,
    format_id: formatId,
    format_name: formatName,
  };
};

export const buildManualInvoiceDetailLines = (productsToAdd, supplierId, options = {}) => {
  const products = Array.isArray(productsToAdd) ? productsToAdd : [];
  const dueDate = options?.dueDate || new Date().toISOString().split('T')[0];
  const createRowId = typeof options?.createRowId === 'function'
    ? options.createRowId
    : (productId, index) => `manual-${productId ?? 'x'}-${Date.now()}-${index}`;

  return products.map((product, index) => {
    const prices = Array.isArray(product?.prices) ? product.prices : [];
    const firstPrice = prices[0] || null;
    const rawPrice = firstPrice?.price ?? product?.price ?? product?.finalPrice ?? '';
    return normalizeInvoiceLineFormat({
      clientRowId: createRowId(product?.id, index),
      product_id: product?.id,
      product,
      supplier_id: product?.supplier_id ?? product?.supplier?.id ?? supplierId ?? null,
      code: product?.code,
      description: product?.description,
      quantity: 1,
      quantityRe: 1,
      checked: true,
      prformat_id: product?.prformat_id ?? product?.format_id ?? product?.prformat?.id ?? null,
      prformat: product?.prformat ?? null,
      price: rawPrice === '' ? '' : String(rawPrice),
      priceOptions: prices,
      selectedPriceId: String(firstPrice?.id ?? 'custom'),
      finalPrice: toNumber(rawPrice, 0),
      dueDate,
      source_orders: [],
    });
  });
};

const getFormatSignatures = (row) => {
  const signatures = [];
  const formatId = getFormatId(row);
  if (formatId) signatures.push(`id:${formatId}`);

  const formatName = getFormatName(row);
  if (formatName) signatures.push(`name:${formatName}`);

  return signatures.length > 0 ? Array.from(new Set(signatures)) : ['none'];
};

const getFormatSignature = (row) => {
  return getFormatSignatures(row)[0];
};

const formatsAreCompatible = (left, right) => {
  const leftDirectId = getDirectFormatId(left);
  const rightDirectId = getDirectFormatId(right);
  const leftDirectName = getDirectRawFormatName(left);
  const rightDirectName = getDirectRawFormatName(right);
  if (
    isOrderFormatMissing(right)
    && !leftDirectId
    && !leftDirectName
    && !rightDirectId
    && !rightDirectName
  ) {
    return true;
  }

  const leftId = getFormatId(left);
  const rightId = getFormatId(right);
  if (leftId && rightId) return leftId === rightId;

  const leftName = getFormatName(left);
  const rightName = getFormatName(right);
  if (leftName && rightName) return leftName === rightName;
  if (isOrderFormatMissing(right) && !rightId && !rightName) return true;
  if (isOrderFormatMissing(left) && !leftId && !leftName) return true;
  if (leftName || rightName || leftId || rightId) return false;
  return true;
};

const parseDateRank = (value) => {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
};

const clonePoolEntries = (entries) => {
  return (entries || []).map((entry) => ({ ...entry }));
};

const keyByProductAndFormat = (productId, formatSignature) => `${productId}::${formatSignature}`;

export const RECONCILIATION_STATUS_META = {
  CONCILIADO_PARCIAL: { label: 'Conciliado parcial', color: 'success' },
  REVISAR_CONCILIACION: { label: 'Revisar OC', color: 'error' },
  ORDER_FORMAT_MISSING: { label: 'Formato no especificado en OC', color: 'warning' },
  SIN_OC: { label: 'Sin OC', color: 'default' },
  EXCEDENTE: { label: 'Excedente', color: 'warning' },
  PRECIO_DISTINTO: { label: 'Precio distinto', color: 'warning' },
  BONIFICACION_PENDIENTE: { label: 'Bonificación pendiente', color: 'warning' },
  FALTANTE: { label: 'Faltante', color: 'error' },
};

export const getReconciliationStatusItems = (flags, reconciliationMeta = null) => {
  const uniqueFlags = Array.from(new Set(Array.isArray(flags) ? flags : []));
  const allocatedQuantity = Math.max(0, toInt(reconciliationMeta?.allocated_quantity, 0));
  const unassignedQuantity = Math.max(0, toInt(reconciliationMeta?.unassigned_quantity, 0));
  const statusItems = [];

  if (allocatedQuantity > 0 && unassignedQuantity > 0) {
    statusItems.push({
      flag: 'CONCILIADO_PARCIAL',
      ...RECONCILIATION_STATUS_META.CONCILIADO_PARCIAL,
    });
  } else if (allocatedQuantity > 0 || uniqueFlags.length === 0) {
    statusItems.push({ flag: 'CONCILIADO', label: 'Conciliado', color: 'success' });
  }

  const flagItems = uniqueFlags.map((flag) => {
    const meta = RECONCILIATION_STATUS_META[flag];
    if (!meta) return null;
    const label = flag === 'EXCEDENTE' && Number(reconciliationMeta?.unassigned_quantity || 0) > 0
      ? `${meta.label} ${Number(reconciliationMeta.unassigned_quantity)}`
      : meta.label;
    return { flag, label, color: meta.color };
  }).filter(Boolean);

  return [...statusItems, ...flagItems];
};

export const getReconciliationStatusLabels = (flags, reconciliationMeta = null) => {
  return getReconciliationStatusItems(flags, reconciliationMeta).map((item) => item.label);
};

const isCandidateOrderOpen = (order) => {
  if (!order) return false;
  if (order?.is_active === false || order?.isActive === false) return false;

  const rawStatus = order?.orderStatus ?? order?.status ?? order?.order_status ?? '';
  const status = normalizeText(rawStatus).toUpperCase();
  if (!status) return true;

  const openStatuses = new Set([
    'PE',
    'EP',
    'OPEN',
    'PARTIAL',
    'PENDING',
    'PENDIENTE',
    'ABIERTA',
    'ABIERTO',
    'VIGENTE',
    'ENTREGA PARCIAL',
    'PENDIENTE DE ENTREGA',
  ]);
  const closedStatuses = new Set([
    'FI',
    'EX',
    'FINISHED',
    'CLOSED',
    'CLOSE',
    'CANCELLED',
    'CANCELED',
    'VOID',
    'VOIDED',
    'EXPIRED',
    'FINALIZADO',
    'CERRADA',
    'CERRADO',
    'CANCELADA',
    'CANCELADO',
    'ANULADA',
    'ANULADO',
    'VENCIDA',
    'VENCIDO',
  ]);

  if (closedStatuses.has(status)) return false;
  return openStatuses.has(status);
};

const getQuantity = (row, fallback = 0) => {
  return toInt(
    row?.quantity ??
      row?.cantidad ??
      row?.amount ??
      row?.qty ??
      row?.ordered_quantity ??
      row?.original_quantity,
    fallback
  );
};

const getPendingQuantity = (row) => {
  return toInt(
    row?.pending_quantity ??
      row?.remaining_quantity ??
      row?.available_quantity ??
      row?.cantidad_pendiente ??
      row?.quantity_pending,
    getQuantity(row, 0) - toInt(row?.taken_quantity ?? row?.received_quantity ?? row?.received ?? row?.cantidad_recibida, 0)
  );
};

const getPrice = (row, fallback = 0) => {
  return toNumber(
    row?.price ??
      row?.order_price ??
      row?.finalPrice ??
      row?.final_price ??
      row?.listPrice ??
      row?.list_price ??
      row?.negotiated_price ??
      row?.precio,
    fallback
  );
};

export const normalizeCandidateOrders = (candidateOrders) => {
  return (Array.isArray(candidateOrders) ? candidateOrders : []).map((order) => {
    const parsedOrderId = toInt(order?.id ?? order?.order_id ?? order?.orderId, 0);
    const orderId = parsedOrderId || order?.id || null;
    const supplierId = getSupplierId(order);
    const orderDate = order?.order_date ?? order?.createdAt ?? order?.dateTime ?? null;
    const rawDetails = Array.isArray(order?.order_details)
      ? order.order_details
      : (Array.isArray(order?.orderDetails) ? order.orderDetails : []);

    const orderDetails = rawDetails.map((detail) => {
      const directFormatId = getDirectFormatId(detail);
      const directFormatName = getDirectRawFormatName(detail);
      const inferredFormatId = getInferredFormatId(detail);
      const inferredFormatName = getInferredRawFormatName(detail);
      const orderFormatMissing = detail?.order_format_missing === true || !directFormatId;
      const formatId = directFormatId ?? inferredFormatId;
      const formatName = directFormatName ?? inferredFormatName;
      const productId = getProductId(detail);
      return {
        ...detail,
        order_id: toInt(detail?.order_id ?? orderId, 0) || orderId,
        order_detail_id: toInt(detail?.order_detail_id ?? detail?.detail_id ?? detail?.id, 0) || null,
        supplier_id: getSupplierId(detail) ?? supplierId,
        product_id: productId,
        product_code: detail?.product_code ?? detail?.product?.code ?? detail?.code ?? null,
        format_id: formatId,
        format_name: formatName,
        order_detail_format_id: directFormatId,
        order_detail_format_name: directFormatName,
        inferred_format_id: orderFormatMissing ? inferredFormatId : null,
        inferred_format_name: orderFormatMissing ? inferredFormatName : null,
        order_format_missing: orderFormatMissing,
        pending_quantity: Math.max(0, getPendingQuantity(detail)),
        order_price: getPrice(detail, 0),
        order_date: detail?.order_date ?? orderDate,
      };
    });

    return {
      ...order,
      id: orderId,
      order_id: orderId,
      supplier_id: supplierId,
      order_date: orderDate,
      order_details: orderDetails,
    };
  });
};

const allocateFromEntries = (entries, requestedQuantity) => {
  let pending = Math.max(0, toInt(requestedQuantity, 0));
  const allocations = [];

  for (let i = 0; i < entries.length && pending > 0; i += 1) {
    const candidate = entries[i];
    const available = Math.max(0, toInt(candidate?.remaining, 0));
    if (available <= 0) continue;

    const assigned = Math.min(available, pending);
    if (assigned <= 0) continue;

    candidate.remaining = available - assigned;
    pending -= assigned;

    allocations.push({
      order_id: candidate.order_id,
      detail_id: candidate.detail_id ?? null,
      order_bonus_id: candidate.order_bonus_id ?? null,
      supplier_id: candidate.supplier_id ?? null,
      product_id: candidate.product_id ?? null,
      product_code: candidate.product_code ?? null,
      quantity: assigned,
      format_id: candidate.format_id ?? null,
      format_name: candidate.format_name ?? null,
      order_detail_format_id: candidate.order_detail_format_id ?? null,
      order_detail_format_name: candidate.order_detail_format_name ?? null,
      inferred_format_id: candidate.inferred_format_id ?? null,
      inferred_format_name: candidate.inferred_format_name ?? null,
      order_format_missing: candidate.order_format_missing === true,
      pending_quantity: available,
      order_status: candidate.order_status,
      order_created_at: candidate.order_created_at,
      suggested_price: candidate.suggested_price ?? null,
    });
  }

  return { allocations, unassigned: pending };
};

const buildReconciliationPools = (candidateOrders) => {
  const orders = normalizeCandidateOrders(candidateOrders);
  orders.sort((a, b) => parseDateRank(a?.order_date || a?.createdAt || a?.dateTime) - parseDateRank(b?.order_date || b?.createdAt || b?.dateTime));

  const detailByKey = new Map();
  const detailByProduct = new Map();
  const detailAliasByKey = new Map();
  const bonusByKey = new Map();
  const bonusByProduct = new Map();
  const bonusAliasByKey = new Map();

  orders.forEach((order) => {
    const orderId = toInt(order?.id, 0);
    if (!orderId) return;
    if (!isCandidateOrderOpen(order)) return;

    const supplierId = getSupplierId(order);
    const orderStatus = order?.orderStatus;
    const orderCreatedAt = order?.order_date || order?.createdAt || order?.dateTime || null;

    const details = Array.isArray(order?.order_details)
      ? order.order_details
      : (Array.isArray(order?.orderDetails) ? order.orderDetails : []);
    details.forEach((detail) => {
      const productId = getProductId(detail);
      if (!productId) return;
      const detailId = toInt(detail?.id ?? detail?.detail_id ?? detail?.order_detail_id, 0) || null;
      if (!detailId) return;

      const remaining = Math.max(0, getPendingQuantity(detail));
      if (remaining <= 0) return;

      const formatSignatures = getFormatSignatures(detail);
      const canonicalSignature = formatSignatures[0];
      const key = keyByProductAndFormat(productId, canonicalSignature);
      const suggestedPrice = getPrice(detail, 0);
      const orderFormatMissing = isOrderFormatMissing(detail);

      const entry = {
        order_id: orderId,
        detail_id: detailId,
        product_id: productId,
        product_code: detail?.product_code ?? detail?.product?.code ?? detail?.code ?? null,
        supplier_id: supplierId ?? getSupplierId(detail),
        format_id: getFormatId(detail),
        format_name: getFormatName(detail),
        order_detail_format_id: getDirectFormatId(detail),
        order_detail_format_name: getDirectRawFormatName(detail),
        inferred_format_id: orderFormatMissing ? getInferredFormatId(detail) : null,
        inferred_format_name: orderFormatMissing ? getInferredRawFormatName(detail) : null,
        order_format_missing: orderFormatMissing,
        remaining,
        suggested_price: Number.isFinite(suggestedPrice) ? suggestedPrice : 0,
        order_status: orderStatus,
        order_created_at: orderCreatedAt,
      };

      if (!entry.order_format_missing) {
        if (!detailByKey.has(key)) detailByKey.set(key, []);
        detailByKey.get(key).push(entry);
        formatSignatures.forEach((signature) => {
          detailAliasByKey.set(keyByProductAndFormat(productId, signature), key);
        });
      }

      if (!detailByProduct.has(productId)) detailByProduct.set(productId, []);
      detailByProduct.get(productId).push({ ...entry });
    });

    const bonuses = Array.isArray(order?.order_bonus)
      ? order.order_bonus
      : (Array.isArray(order?.orderBonus)
        ? order.orderBonus
        : (Array.isArray(order?.order_bonuses) ? order.order_bonuses : []));
    bonuses.forEach((bonus) => {
      const productId = getProductId(bonus);
      if (!productId) return;
      const orderBonusId = toInt(bonus?.id ?? bonus?.order_bonus_id ?? bonus?.orderBonusId, 0) || null;
      if (!orderBonusId) return;

      const remaining = Math.max(0, getPendingQuantity(bonus));
      if (remaining <= 0) return;

      const formatSignatures = getFormatSignatures(bonus);
      const canonicalSignature = formatSignatures[0];
      const key = keyByProductAndFormat(productId, canonicalSignature);

      const entry = {
        order_id: orderId,
        order_bonus_id: orderBonusId,
        product_id: productId,
        product_code: bonus?.product_code ?? bonus?.product?.code ?? bonus?.code ?? null,
        supplier_id: supplierId ?? getSupplierId(bonus),
        format_id: getFormatId(bonus),
        format_name: getFormatName(bonus),
        remaining,
        order_status: orderStatus,
        order_created_at: orderCreatedAt,
      };

      if (!bonusByKey.has(key)) bonusByKey.set(key, []);
      bonusByKey.get(key).push(entry);
      formatSignatures.forEach((signature) => {
        bonusAliasByKey.set(keyByProductAndFormat(productId, signature), key);
      });

      if (!bonusByProduct.has(productId)) bonusByProduct.set(productId, []);
      bonusByProduct.get(productId).push({ ...entry });
    });
  });

  const sortByAge = (entryA, entryB) => parseDateRank(entryA.order_created_at) - parseDateRank(entryB.order_created_at);
  detailByKey.forEach((list, key) => detailByKey.set(key, [...list].sort(sortByAge)));
  detailByProduct.forEach((list, key) => detailByProduct.set(key, [...list].sort(sortByAge)));
  bonusByKey.forEach((list, key) => bonusByKey.set(key, [...list].sort(sortByAge)));
  bonusByProduct.forEach((list, key) => bonusByProduct.set(key, [...list].sort(sortByAge)));

  return {
    detailByKey,
    detailByProduct,
    detailAliasByKey,
    bonusByKey,
    bonusByProduct,
    bonusAliasByKey,
  };
};

const createMutablePools = (candidateOrders) => {
  const base = buildReconciliationPools(candidateOrders);

  const cloneMap = (map) => {
    const next = new Map();
    map.forEach((value, key) => {
      next.set(key, clonePoolEntries(value));
    });
    return next;
  };

  return {
    detailByKey: cloneMap(base.detailByKey),
    detailByProduct: cloneMap(base.detailByProduct),
    detailAliasByKey: new Map(base.detailAliasByKey),
    bonusByKey: cloneMap(base.bonusByKey),
    bonusByProduct: cloneMap(base.bonusByProduct),
    bonusAliasByKey: new Map(base.bonusAliasByKey),
  };
};

const filterEntriesBySupplier = (entries, supplierId) => {
  if (!supplierId) return entries;
  return entries.filter((entry) => {
    const entrySupplierId = toInt(entry?.supplier_id, 0);
    return entrySupplierId === supplierId;
  });
};

const chooseEntries = (poolByKey, aliasByKey, productId, row, fallbackByProduct = null) => {
  if (!productId) return [];
  const signatures = getFormatSignatures(row);
  const supplierId = getSupplierId(row);
  let exactEntries = [];

  for (const signature of signatures) {
    const exactKey = keyByProductAndFormat(productId, signature);
    const canonicalKey = aliasByKey?.get(exactKey) || exactKey;
    const exact = poolByKey.get(canonicalKey);
    if (Array.isArray(exact) && exact.length > 0) {
      const filtered = filterEntriesBySupplier(exact, supplierId)
        .filter((entry) => formatsAreCompatible(row, entry));
      if (filtered.length > 0) {
        exactEntries = filtered;
        break;
      }
    }
  }

  const fallbackEntries = filterEntriesBySupplier(fallbackByProduct?.get(productId) || [], supplierId)
    .filter((entry) => entry?.order_format_missing === true)
    .filter((entry) => formatsAreCompatible(row, entry));

  return [...exactEntries, ...fallbackEntries];
};

const findCompatibleCandidates = (candidateOrders, row, type) => {
  const productId = getProductId(row);
  if (!productId) return [];

  const supplierId = getSupplierId(row);
  return (Array.isArray(candidateOrders) ? candidateOrders : []).flatMap((order) => {
    if (!isCandidateOrderOpen(order)) return [];
    const orderSupplierId = getSupplierId(order);
    if (supplierId && supplierId !== orderSupplierId) return [];

    const rows = type === 'bonus'
      ? (Array.isArray(order?.order_bonus) ? order.order_bonus : [])
      : (Array.isArray(order?.order_details) ? order.order_details : []);

    return rows.filter((candidate) => {
      if (getProductId(candidate) !== productId) return false;
      if (getPendingQuantity(candidate) <= 0) return false;
      return formatsAreCompatible(row, candidate);
    });
  });
};

const buildDetailFlags = ({
  requestedQty,
  allocatedQty,
  price,
  suggestedPrice,
  confirmedQty,
  requiresReview,
  orderFormatMissing,
}) => {
  const flags = [];

  if (requiresReview) {
    flags.push('REVISAR_CONCILIACION');
  }

  if (orderFormatMissing) {
    flags.push('ORDER_FORMAT_MISSING');
  }

  if (allocatedQty <= 0) {
    flags.push('SIN_OC');
  }

  if (allocatedQty < requestedQty) {
    flags.push('EXCEDENTE');
  }

  if (suggestedPrice != null && Math.abs(toNumber(price, 0) - toNumber(suggestedPrice, 0)) > 0.009) {
    flags.push('PRECIO_DISTINTO');
  }

  if (confirmedQty < requestedQty) {
    flags.push('FALTANTE');
  }

  return flags;
};

const buildBonusFlags = ({ requestedQty, allocatedQty, confirmedQty, requiresReview }) => {
  const flags = [];

  if (requiresReview) {
    flags.push('REVISAR_CONCILIACION');
  }

  if (allocatedQty <= 0) {
    flags.push('SIN_OC');
  }

  if (allocatedQty < requestedQty) {
    flags.push('BONIFICACION_PENDIENTE');
  }

  if (confirmedQty < requestedQty) {
    flags.push('FALTANTE');
  }

  return flags;
};

const computeSuggestedPrice = (allocations) => {
  if (!Array.isArray(allocations) || allocations.length === 0) return null;

  let qtyAcc = 0;
  let priceAcc = 0;
  allocations.forEach((item) => {
    const qty = toInt(item?.quantity, 0);
    const price = toNumber(item?.suggested_price, NaN);
    if (qty <= 0 || Number.isNaN(price)) return;
    qtyAcc += qty;
    priceAcc += qty * price;
  });

  if (qtyAcc <= 0) return null;
  return priceAcc / qtyAcc;
};

export const reconcileInvoiceDraft = (invoiceDetail, invoiceBonus, candidateOrders) => {
  const detailLines = Array.isArray(invoiceDetail) ? invoiceDetail : [];
  const bonusLines = Array.isArray(invoiceBonus) ? invoiceBonus : [];
  const normalizedCandidateOrders = normalizeCandidateOrders(candidateOrders);
  const pools = createMutablePools(normalizedCandidateOrders);

  const usedOrderIds = new Set();
  const reviewWarnings = [];

  const reconciledDetails = detailLines.map((line) => {
    const normalizedLine = normalizeInvoiceLineFormat(line);
    const quantity = Math.max(0, getQuantity(line, 0));
    const confirmedQty = Math.max(0, Math.min(quantity, toInt(line?.quantityRe, quantity)));
    const productId = getProductId(line);
    const formatSignature = getFormatSignature(line);

    const entries = chooseEntries(
      pools.detailByKey,
      pools.detailAliasByKey,
      productId,
      line,
      pools.detailByProduct,
    );
    const compatibleCandidates = findCompatibleCandidates(normalizedCandidateOrders, line, 'detail');
    const { allocations, unassigned } = allocateFromEntries(entries, quantity);

    allocations.forEach((allocation) => {
      if (allocation?.order_id) usedOrderIds.add(allocation.order_id);
    });

    const allocatedQty = allocations.reduce((sum, item) => sum + toInt(item?.quantity, 0), 0);
    const suggestedPrice = computeSuggestedPrice(allocations);
    const requiresReview = quantity > 0 && compatibleCandidates.length > 0 && allocatedQty <= 0;
    if (requiresReview) {
      reviewWarnings.push({
        type: 'detail',
        product_id: productId,
        format_signature: formatSignature,
        quantity,
      });
    }
    const flags = buildDetailFlags({
      requestedQty: quantity,
      allocatedQty,
      price: line?.price,
      suggestedPrice,
      confirmedQty,
      requiresReview,
      orderFormatMissing: allocations.some((allocation) => allocation?.order_format_missing === true),
    });

    return {
      ...normalizedLine,
      quantity,
      quantityRe: confirmedQty,
      reconciliation: {
        allocations,
        allocated_quantity: allocatedQty,
        unassigned_quantity: unassigned,
        suggested_price: suggestedPrice,
        compatible_candidate_count: compatibleCandidates.length,
        requires_review: requiresReview,
        flags,
      },
    };
  });

  const reconciledBonuses = bonusLines.map((line) => {
    const normalizedLine = normalizeInvoiceLineFormat(line);
    const quantity = Math.max(0, getQuantity(line, 0));
    const confirmedQty = Math.max(0, Math.min(quantity, toInt(line?.quantityRe, quantity)));
    const productId = getProductId(line);
    const formatSignature = getFormatSignature(line);

    const explicitOrderBonusId = toInt(line?.order_bonus_id ?? line?.order_bonus?.id, 0);

    let allocations = [];
    let unassigned = quantity;

    if (explicitOrderBonusId > 0) {
      const candidates = chooseEntries(pools.bonusByKey, pools.bonusAliasByKey, productId, line);
      const matched = candidates.find((entry) => toInt(entry?.order_bonus_id, 0) === explicitOrderBonusId);
      if (matched) {
        const assigned = Math.min(Math.max(0, matched.remaining), quantity);
        matched.remaining -= assigned;
        unassigned -= assigned;
        allocations = assigned > 0
          ? [{
              order_id: matched.order_id,
              order_bonus_id: matched.order_bonus_id,
              supplier_id: matched.supplier_id ?? null,
              product_id: matched.product_id,
              product_code: matched.product_code,
              quantity: assigned,
              format_id: matched.format_id,
              format_name: matched.format_name,
              pending_quantity: Math.max(0, matched.remaining + assigned),
              order_status: matched.order_status,
              order_created_at: matched.order_created_at,
            }]
          : [];
      }
    }

    if (allocations.length === 0) {
      const entries = chooseEntries(pools.bonusByKey, pools.bonusAliasByKey, productId, line);
      const allocated = allocateFromEntries(entries, quantity);
      allocations = allocated.allocations;
      unassigned = allocated.unassigned;
    }

    allocations.forEach((allocation) => {
      if (allocation?.order_id) usedOrderIds.add(allocation.order_id);
    });

    const allocatedQty = allocations.reduce((sum, item) => sum + toInt(item?.quantity, 0), 0);
    const compatibleCandidates = findCompatibleCandidates(normalizedCandidateOrders, line, 'bonus');
    const requiresReview = quantity > 0 && compatibleCandidates.length > 0 && allocatedQty <= 0;
    if (requiresReview) {
      reviewWarnings.push({
        type: 'bonus',
        product_id: productId,
        format_signature: formatSignature,
        quantity,
      });
    }
    const flags = buildBonusFlags({
      requestedQty: quantity,
      allocatedQty,
      confirmedQty,
      requiresReview,
    });

    return {
      ...normalizedLine,
      quantity,
      quantityRe: confirmedQty,
      reconciliation: {
        allocations,
        allocated_quantity: allocatedQty,
        unassigned_quantity: unassigned,
        compatible_candidate_count: compatibleCandidates.length,
        requires_review: requiresReview,
        flags,
      },
    };
  });

  const pendingExpectedBonuses = [];
  pools.bonusByKey.forEach((entries, key) => {
    const remaining = entries.reduce((sum, entry) => sum + Math.max(0, toInt(entry?.remaining, 0)), 0);
    if (remaining <= 0) return;

    const [productText, formatSignature] = String(key || '').split('::');
    pendingExpectedBonuses.push({
      product_id: toInt(productText, 0) || null,
      format_signature: formatSignature || 'none',
      pending_quantity: remaining,
    });
  });

  return {
    details: reconciledDetails,
    bonuses: reconciledBonuses,
    usedOrderIds: Array.from(usedOrderIds),
    pendingExpectedBonuses,
    reviewWarnings,
    hasReviewWarnings: reviewWarnings.length > 0,
  };
};

export const buildReconciliationDiagnostics = (invoiceDetail, candidateOrders, reconciledDetails = []) => {
  const orders = normalizeCandidateOrders(candidateOrders);
  const candidates = orders.flatMap((order) => {
    return (order?.order_details || []).map((detail) => ({
      order_id: detail?.order_id ?? order?.id ?? null,
      order_detail_id: detail?.order_detail_id ?? detail?.id ?? null,
      supplier_id: detail?.supplier_id ?? order?.supplier_id ?? null,
      product_id: getProductId(detail),
      product_code: detail?.product_code ?? detail?.product?.code ?? null,
      format_id: getFormatId(detail),
      format_name: getRawFormatName(detail),
      order_detail_format_id: getDirectFormatId(detail),
      order_detail_format_name: getDirectRawFormatName(detail),
      inferred_format_id: getInferredFormatId(detail),
      inferred_format_name: getInferredRawFormatName(detail),
      order_format_missing: isOrderFormatMissing(detail),
      pending_quantity: getPendingQuantity(detail),
      order_price: getPrice(detail, 0),
      order_date: detail?.order_date ?? order?.order_date ?? null,
    }));
  });

  return (Array.isArray(invoiceDetail) ? invoiceDetail : []).map((line, index) => {
    const supplierId = getSupplierId(line);
    const productId = getProductId(line);
    const productCandidates = candidates.filter((candidate) => candidate.product_id === productId);
    const supplierCandidates = productCandidates.filter((candidate) => {
      return !supplierId || toInt(candidate.supplier_id, 0) === supplierId;
    });
    const formatCandidates = supplierCandidates.filter((candidate) => formatsAreCompatible(line, candidate));
    const pendingCandidates = formatCandidates.filter((candidate) => candidate.pending_quantity > 0);
    const reconciliation = reconciledDetails?.[index]?.reconciliation || null;
    const allocations = Array.isArray(reconciliation?.allocations) ? reconciliation.allocations : [];

    let reason = 'matched';
    if (allocations.length === 0) {
      if (orders.length === 0) reason = 'no candidate orders';
      else if (productCandidates.length === 0) reason = 'product not found in candidate orders';
      else if (supplierCandidates.length === 0) reason = 'supplier mismatch';
      else if (formatCandidates.length === 0) reason = 'format mismatch';
      else if (pendingCandidates.length === 0) reason = 'no pending quantity';
      else reason = 'compatible candidate found but no allocation generated';
    }

    return {
      supplier_id: supplierId,
      product_id: productId,
      format_id: getFormatId(line),
      format_name: getRawFormatName(line),
      quantity: getQuantity(line, 0),
      candidate_orders_count: orders.length,
      candidate_order_ids: orders.map((order) => order?.id).filter(Boolean),
      candidate_order_details: candidates,
      match: allocations.length > 0,
      reason,
      allocations,
    };
  });
};

export const buildReconciledInvoicePayload = (invoiceDetail, invoiceBonus, candidateOrders) => {
  const reconciliation = reconcileInvoiceDraft(invoiceDetail, invoiceBonus, candidateOrders);
  const payloadLines = buildInvoicePayloadLines(reconciliation.details, reconciliation.bonuses);
  const payloadDiagnostics = buildInvoicePayloadDiagnostics(payloadLines, reconciliation);
  return {
    reconciliation,
    payloadLines,
    orderIds: reconciliation.usedOrderIds,
    reviewWarnings: [
      ...reconciliation.reviewWarnings,
      ...(payloadLines.validation_warnings || []),
    ],
    payloadDiagnostics,
  };
};

export const buildInvoicePayloadLines = (reconciledDetails, reconciledBonuses) => {
  const detailPayload = [];
  const bonusPayload = [];
  const validationWarnings = [];

  const allocationValidationReason = (line, allocation) => {
    const lineProductId = getProductId(line);
    const allocationProductId = getProductId(allocation);
    if (allocationProductId && allocationProductId !== lineProductId) return 'product mismatch';
    if (!formatsAreCompatible(line, allocation)) return 'format mismatch';
    const lineSupplierId = getSupplierId(line);
    const allocationSupplierId = getSupplierId(allocation);
    if (lineSupplierId && allocationSupplierId !== lineSupplierId) {
      return 'supplier mismatch';
    }

    const quantity = Math.max(0, toInt(allocation?.quantity, 0));
    if (quantity <= 0) return 'invalid allocation quantity';
    const hasPendingQuantity = allocation?.pending_quantity !== undefined
      && allocation?.pending_quantity !== null
      && allocation?.pending_quantity !== '';
    const pendingQuantity = Math.max(0, toInt(allocation?.pending_quantity, 0));
    if (hasPendingQuantity && quantity > pendingQuantity) return 'allocation exceeds pending quantity';
    return null;
  };

  (Array.isArray(reconciledDetails) ? reconciledDetails : []).forEach((line) => {
    const quantity = Math.max(0, toInt(line?.quantity, 0));
    if (quantity <= 0) return;

    const productId = getProductId(line);
    if (!productId) return;

    const sourceAllocations = Array.isArray(line?.reconciliation?.allocations) ? line.reconciliation.allocations : [];
    const allocations = sourceAllocations.filter((allocation) => {
      const reason = allocationValidationReason(line, allocation);
      if (!reason) return true;
      validationWarnings.push({
        type: 'detail_allocation',
        product_id: productId,
        format_id: getFormatId(line),
        order_detail_id: toInt(allocation?.detail_id ?? allocation?.order_detail_id, 0) || null,
        reason,
      });
      return false;
    });
    const allocationFormatId = allocations
      .map((allocation) => toInt(allocation?.format_id, 0))
      .find((formatId) => formatId > 0);
    const normalizedAllocations = allocations
      .map((allocation) => ({
        order_detail_id: toInt(allocation?.detail_id ?? allocation?.order_detail_id, 0) || null,
        detail_id: toInt(allocation?.detail_id ?? allocation?.order_detail_id, 0) || null,
        order_id: toInt(allocation?.order_id, 0) || null,
        quantity: Math.max(0, toInt(allocation?.quantity, 0)),
        ...(allocation?.order_format_missing === true ? { order_format_missing: true } : {}),
      }))
      .filter((allocation) => allocation.order_detail_id && allocation.quantity > 0);

    detailPayload.push({
      id: line?.id ?? undefined,
      client_line_id: line?.client_line_id || line?.clientRowId || line?.client_line || undefined,
      product_id: productId,
      quantity,
      quantityRe: Math.max(0, Math.min(toInt(line?.quantityRe ?? quantity, quantity), quantity)),
      price: toNumber(line?.price, toNumber(line?.finalPrice, 0)),
      dueDate: line?.dueDate,
      prformat_id: getFormatId(line) || allocationFormatId || null,
      order_detail_id: normalizedAllocations.length === 1 ? normalizedAllocations[0].order_detail_id : null,
      allocations: normalizedAllocations,
      is_bonus: Boolean(line?.is_bonus),
    });
  });

  (Array.isArray(reconciledBonuses) ? reconciledBonuses : []).forEach((line) => {
    const quantity = Math.max(0, toInt(line?.quantity, 0));
    if (quantity <= 0) return;

    const productId = getProductId(line);
    if (!productId) return;

    const sourceAllocations = Array.isArray(line?.reconciliation?.allocations) ? line.reconciliation.allocations : [];
    const allocations = sourceAllocations.filter((allocation) => {
      const reason = allocationValidationReason(line, allocation);
      if (!reason) return true;
      validationWarnings.push({
        type: 'bonus_allocation',
        product_id: productId,
        format_id: getFormatId(line),
        order_bonus_id: toInt(allocation?.order_bonus_id, 0) || null,
        reason,
      });
      return false;
    });
    const allocationFormatId = allocations
      .map((allocation) => toInt(allocation?.format_id, 0))
      .find((formatId) => formatId > 0);
    const normalizedAllocations = allocations
      .map((allocation) => ({
        order_bonus_id: toInt(allocation?.order_bonus_id, 0) || null,
        order_id: toInt(allocation?.order_id, 0) || null,
        quantity: Math.max(0, toInt(allocation?.quantity, 0)),
      }))
      .filter((allocation) => allocation.order_bonus_id && allocation.quantity > 0);

    bonusPayload.push({
      id: line?.id ?? undefined,
      client_line_id: line?.client_line_id || line?.clientRowId || line?.client_line || undefined,
      product_id: productId,
      quantity,
      quantityRe: Math.max(0, Math.min(toInt(line?.quantityRe ?? quantity, quantity), quantity)),
      prformat_id: getFormatId(line) || allocationFormatId || null,
      order_bonus_id: normalizedAllocations.length === 1
        ? normalizedAllocations[0].order_bonus_id
        : (toInt(line?.order_bonus_id ?? line?.order_bonus?.id, 0) || null),
      allocations: normalizedAllocations,
      comment: line?.comment || line?.reason || '',
      is_manual: Boolean(line?.is_manual ?? normalizedAllocations.length === 0),
    });
  });

  return {
    invoice_detail: detailPayload,
    invoice_bonus: bonusPayload,
    validation_warnings: validationWarnings,
  };
};

export const buildInvoicePayloadDiagnostics = (payloadLines, reconciliation) => {
  const detailPayload = Array.isArray(payloadLines?.invoice_detail) ? payloadLines.invoice_detail : [];
  const reconciledDetails = Array.isArray(reconciliation?.details) ? reconciliation.details : [];

  return detailPayload.map((payloadLine, index) => {
    const reconciledLine = reconciledDetails[index] || {};
    const meta = reconciledLine?.reconciliation || {};
    const sourceAllocations = Array.isArray(meta?.allocations) ? meta.allocations : [];
    return {
      product_id: payloadLine?.product_id ?? getProductId(reconciledLine),
      product_code: reconciledLine?.code ?? reconciledLine?.product?.code ?? null,
      product_name: reconciledLine?.description ?? reconciledLine?.product?.description ?? null,
      supplier_id: getSupplierId(reconciledLine),
      format_id: getFormatId(reconciledLine),
      prformat_id: payloadLine?.prformat_id ?? null,
      formato_id: reconciledLine?.formato_id ?? null,
      format_name: getRawFormatName(reconciledLine),
      quantity: payloadLine?.quantity ?? getQuantity(reconciledLine, 0),
      allocations: (payloadLine?.allocations || []).map((allocation) => {
        const source = sourceAllocations.find((item) => {
          return toInt(item?.detail_id ?? item?.order_detail_id, 0) === toInt(allocation?.order_detail_id, 0);
        }) || {};
        return {
          ...allocation,
          order_detail_supplier_id: getSupplierId(source),
          order_detail_product_id: getProductId(source),
          order_detail_format_id: getDirectFormatId(source),
          order_detail_format_name: getDirectRawFormatName(source),
          inferred_format_id: getInferredFormatId(source),
          inferred_format_name: getInferredRawFormatName(source),
          order_format_missing: isOrderFormatMissing(source),
          pending_quantity: source?.pending_quantity ?? null,
        };
      }),
      status: getReconciliationStatusLabels(meta?.flags || [], meta),
    };
  });
};

export const calculateDraftTotals = (invoiceDetail, invoiceAdjustment) => {
  const lines = Array.isArray(invoiceDetail) ? invoiceDetail : [];
  const subtotal = lines.reduce((sum, line) => {
    const qty = Math.max(0, toInt(line?.quantity, 0));
    const price = toNumber(line?.price, toNumber(line?.finalPrice, 0));
    return sum + qty * price;
  }, 0);

  const adjustment = toNumber(invoiceAdjustment, 0);
  const total = subtotal + adjustment;

  return {
    subtotal,
    adjustment,
    total,
  };
};

const normalizeDueDate = (value) => {
  if (!value) return '';
  const text = String(value);
  if (text.includes('T')) return text.split('T')[0];
  return text;
};

const mergeSourceOrders = (left, right, type) => {
  const merged = new Map();
  const keyFor = (src) => {
    const orderId = src?.order_id ?? 'x';
    if (type === 'bonus') {
      return `o:${orderId}:b:${src?.order_bonus_id ?? 'x'}`;
    }
    return `o:${orderId}:d:${src?.detail_id ?? 'x'}`;
  };

  [...(Array.isArray(left) ? left : []), ...(Array.isArray(right) ? right : [])].forEach((src) => {
    if (!src) return;
    const key = keyFor(src);
    const prev = merged.get(key);
    const qty = Math.max(0, toInt(src?.taken_quantity ?? src?.quantity ?? 0, 0));
    if (!prev) {
      merged.set(key, {
        ...src,
        taken_quantity: qty,
      });
      return;
    }
    merged.set(key, {
      ...prev,
      taken_quantity: Math.max(0, toInt(prev?.taken_quantity ?? 0, 0) + qty),
    });
  });

  return Array.from(merged.values());
};

const buildDetailMergeKey = (line) => {
  const productId = getProductId(line) ?? 'x';
  const formatSignature = getFormatSignature(line);
  const price = toNumber(line?.price ?? line?.finalPrice, 0).toFixed(4);
  const dueDate = normalizeDueDate(line?.dueDate);
  const bonusFlag = Boolean(line?.is_bonus) ? '1' : '0';
  return `${productId}::${formatSignature}::${price}::${dueDate}::${bonusFlag}`;
};

const buildBonusMergeKey = (line) => {
  const productId = getProductId(line) ?? 'x';
  const formatSignature = getFormatSignature(line);
  const comment = String(line?.comment ?? line?.reason ?? '').trim().toLowerCase();
  return `${productId}::${formatSignature}::${comment}`;
};

export const mergeInvoiceLinesForDraft = (invoiceDetail, invoiceBonus) => {
  const details = Array.isArray(invoiceDetail) ? invoiceDetail : [];
  const bonuses = Array.isArray(invoiceBonus) ? invoiceBonus : [];

  const mergedDetailMap = new Map();
  details.forEach((line, index) => {
    const key = buildDetailMergeKey(line);
    const qty = Math.max(0, toInt(line?.quantity, 0));
    const qreIn = line?.quantityRe ?? line?.quantity ?? 0;
    const qre = Math.max(0, Math.min(qty, toInt(qreIn, qty)));
    const entry = mergedDetailMap.get(key);

    if (!entry) {
      mergedDetailMap.set(key, {
        ...line,
        clientRowId: line?.clientRowId || `merged-d-${index}`,
        quantity: qty,
        quantityRe: qre,
        checked: line?.checked ?? true,
        source_orders: mergeSourceOrders([], line?.source_orders, 'detail'),
        unassigned_quantity: Math.max(0, toInt(line?.unassigned_quantity, 0)),
      });
      return;
    }

    entry.quantity = Math.max(0, toInt(entry.quantity, 0) + qty);
    entry.quantityRe = Math.max(0, toInt(entry.quantityRe, 0) + qre);
    entry.checked = Boolean(entry.checked) && Boolean(line?.checked ?? true);
    entry.source_orders = mergeSourceOrders(entry.source_orders, line?.source_orders, 'detail');
    entry.unassigned_quantity = Math.max(
      0,
      toInt(entry.unassigned_quantity, 0) + Math.max(0, toInt(line?.unassigned_quantity, 0))
    );
    mergedDetailMap.set(key, entry);
  });

  const mergedBonusMap = new Map();
  bonuses.forEach((line, index) => {
    const key = buildBonusMergeKey(line);
    const qty = Math.max(0, toInt(line?.quantity, 0));
    const qreIn = line?.quantityRe ?? line?.quantity ?? 0;
    const qre = Math.max(0, Math.min(qty, toInt(qreIn, qty)));
    const entry = mergedBonusMap.get(key);

    if (!entry) {
      mergedBonusMap.set(key, {
        ...line,
        clientRowId: line?.clientRowId || `merged-b-${index}`,
        quantity: qty,
        quantityRe: qre,
        checked: line?.checked ?? true,
        source_orders: mergeSourceOrders([], line?.source_orders, 'bonus'),
        unassigned_quantity: Math.max(0, toInt(line?.unassigned_quantity, 0)),
      });
      return;
    }

    entry.quantity = Math.max(0, toInt(entry.quantity, 0) + qty);
    entry.quantityRe = Math.max(0, toInt(entry.quantityRe, 0) + qre);
    entry.checked = Boolean(entry.checked) && Boolean(line?.checked ?? true);
    entry.source_orders = mergeSourceOrders(entry.source_orders, line?.source_orders, 'bonus');
    entry.unassigned_quantity = Math.max(
      0,
      toInt(entry.unassigned_quantity, 0) + Math.max(0, toInt(line?.unassigned_quantity, 0))
    );
    if (!entry.order_bonus_id && line?.order_bonus_id) {
      entry.order_bonus_id = line.order_bonus_id;
    }
    mergedBonusMap.set(key, entry);
  });

  return {
    invoice_detail: Array.from(mergedDetailMap.values()),
    invoice_bonus: Array.from(mergedBonusMap.values()),
  };
};

export const resolveInvoiceTypeCode = (invoiceType, invoiceTypeObject, invoiceTypeId, types) => {
  const normalizeName = (value) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  };

  const fromName = (name) => {
    const normalized = normalizeName(name);
    if (normalized.includes('vale')) return 'V';
    if (normalized.includes('electronica')) return 'FE';
    return 'FC';
  };

  const rawCode = String(invoiceType || '').toUpperCase();
  if (['FC', 'FE', 'V'].includes(rawCode)) return rawCode;

  if (invoiceTypeObject?.code) {
    const objectCode = String(invoiceTypeObject.code).toUpperCase();
    if (['FC', 'FE', 'V'].includes(objectCode)) return objectCode;
  }

  if (invoiceTypeObject?.name) return fromName(invoiceTypeObject.name);

  const selectedById = (types || []).find((item) => String(item?.id) === String(invoiceTypeId));
  if (selectedById?.code) {
    const codeById = String(selectedById.code).toUpperCase();
    if (['FC', 'FE', 'V'].includes(codeById)) return codeById;
  }

  if (selectedById?.name) return fromName(selectedById.name);

  return 'FC';
};

export const toCurrency = (amount) => {
  return new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(amount, 0));
};

export const toDecimalString = (amount) => {
  return toNumber(amount, 0).toFixed(2);
};

export const getRowSearchText = (row) => {
  const code = row?.code ?? row?.product?.code ?? '';
  const description = row?.description ?? row?.product?.description ?? '';
  const format = row?.prformat?.name ?? row?.prformat ?? '';
  return normalizeText(`${code} ${description} ${format}`);
};
